import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';

import { CameraStateFlags } from '@strava/mre';

import { useMRE } from '../../hooks/useMRE';
import { LatLng } from '../../types';

import styles from './PlaceHTMLOnMap.module.scss';

export interface PlaceHTMLOnMapProps {
  latLng: LatLng;
  children: React.ReactNode;
  className?: string;
  allowPointerEvents?: boolean; // If true, allows mouse + touchevents on the HTML element
}

export const PlaceHTMLOnMap = ({
  latLng,
  children,
  className,
  allowPointerEvents = false
}: PlaceHTMLOnMapProps) => {
  const { mapContainerRef, terrainEngine } = useMRE();

  // We set position by using a ref and applying style modifications rather than
  // re-rendering the container in a new position.
  const htmlOnMapRef = useRef<HTMLDivElement>(null);

  const updateLocation = useCallback(() => {
    if (!htmlOnMapRef.current) {
      return;
    }

    if (!terrainEngine || !mapContainerRef.current || !latLng) {
      htmlOnMapRef.current.style.display = 'none';
      return;
    }

    // Get the screen position of the latLng in the terrain engine.
    const screenPos = terrainEngine
      .getCamera()
      .getScreenPosition({ latitude: latLng[0], longitude: latLng[1] });

    if (!screenPos || screenPos.isOccluded) {
      htmlOnMapRef.current.style.display = 'none';
      return;
    }

    // Show the popup
    htmlOnMapRef.current.style.display = 'initial';

    // Transform is more performant than top/left etc; it does not require recalculating position of other elements.
    htmlOnMapRef.current.style.transform = `translateX(-50%) translateY(-50%) translate(${screenPos.x * mapContainerRef.current.clientWidth}px, ${screenPos.y * mapContainerRef.current.clientHeight}px)`;
  }, [latLng, terrainEngine, mapContainerRef]); // ref added to deps array to keep linter happy - has no functional impact

  // when the latLng changes then we trigger an immediate location update
  useEffect(() => {
    if (!latLng || !mapContainerRef.current || !terrainEngine) {
      return;
    }

    updateLocation();
  }, [latLng, mapContainerRef, terrainEngine, updateLocation]);

  // bind updateLocation to camera target or canvas size changes
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const cameraStateListener = {
      stateToObserve: () => CameraStateFlags.TARGET,
      onViewTypeChanged: () => {},
      onTargetChanged: () => {
        updateLocation();
      }
    };

    terrainEngine.getCamera().addStateListener(cameraStateListener);

    window.addEventListener('resize', updateLocation);

    return () => {
      terrainEngine.getCamera().removeStateListener(cameraStateListener);
      window.removeEventListener('resize', updateLocation);
    };
  }, [terrainEngine, updateLocation]);

  return (
    <div
      className={clsx(
        styles.container,
        allowPointerEvents && styles.allowPointerEvents,
        className
      )}
      ref={htmlOnMapRef}
      data-testid="mre-place-html-on-map"
    >
      {children}
    </div>
  );
};
